import * as React from 'react';

import CoursePage from '../CoursePage'
import { Tooltip } from '../../../Shared/index'
import {
  FISHTANK_ELA_ALIGNED_QUILL_ACTIVITIES,
  FISHTANK_ELA_GRADE_7_ACTIVITY_PACKS,
  FISHTANK_ELA_GRADE_8_ACTIVITY_PACKS,
  FISHTANK_IMAGE_CREDIT_TOOLTIP_TEXT,
  LITERATURE_COURSE_PAGE,
  chevronLeftImgSrc,
  fishtankGradeHeaderImgSrc,
} from '../../constants/contentHubs';
import { useTrackChildPageVisit } from '../../hooks/useTrackChildPageVisit';

export const GRADE_CONFIGS = {
  'fishtank-ela-grade-7': {
    localStorageKey: 'fishtankElaGrade7ExpandedUnitTemplateIds',
    title: FISHTANK_ELA_GRADE_7_ACTIVITY_PACKS,
    description: [
      "In Fishtank's 7th grade ELA units, students explore themes related to identity, environment, and community to answer the essential question: How are humans driven by the need to belong? Aligned to the literary texts in these units, Quill's Reading for Evidence and Language Skills activities provide diverse stories from history and today to help students deeply engage with these themes while both learning to write and writing to learn.",
      "Quill Reading for Evidence activities are designed to build knowledge. As students read each nonfiction text, they broaden their understanding of historical context, author biography, and literary and cultural significance to more deeply connect with the works of literature at the center of the Fishtank ELA units.",
      "Each of Quill's Language Skills activities is built around a key grammar skill or concept. These skills are taught using ideas and context from the Fishtank ELA units, so students build core knowledge while deepening their grasp of grammar and syntax.",
    ],
    unitTemplatePath: '/teachers/progress_reports/fishtank_grade_7_unit_templates',
    gradeName: 'Grade 7',
  },
  'fishtank-ela-grade-8': {
    localStorageKey: 'fishtankElaGrade8ExpandedUnitTemplateIds',
    title: FISHTANK_ELA_GRADE_8_ACTIVITY_PACKS,
    description: [
      "In Fishtank's 8th grade ELA units, students explore themes of justice, oppression, and freedom to answer the essential question: How does power influence human behavior? Aligned to the literary texts in these units, Quill's Reading for Evidence and Language Skills activities provide diverse stories from history and today to help students deeply engage with these themes while both learning to write and writing to learn.",
      "Quill Reading for Evidence activities are designed to build knowledge. As students read each nonfiction text, they broaden their understanding of historical context, author biography, and literary and cultural significance to more deeply connect with the works of literature at the center of the Fishtank ELA units.",
      "Each of Quill's Language Skills activities is built around a key grammar skill or concept, like active and passive voice. These skills are taught using ideas and context from the Fishtank ELA units, so students build core knowledge while deepening their grasp of grammar and syntax.",
    ],
    unitTemplatePath: '/teachers/progress_reports/fishtank_grade_8_unit_templates',
    gradeName: 'Grade 8',
  },
}

interface LiteratureGradeLevelProps {
  gradeLevel: string,
  backlinkPath?: string,
  schoolAdmins?: any[],
  subscriptionType?: string
}

const LiteratureGradeLevel = ({ gradeLevel, backlinkPath, subscriptionType }: LiteratureGradeLevelProps) => {
  useTrackChildPageVisit();

  const config = GRADE_CONFIGS[gradeLevel]
  const contentPage = backlinkPath || window.location.href.split(gradeLevel)[0]
  const contentPageLink = <a className="quill-button medium outlined grey icon focus-on-light" href={contentPage}><img alt="" src={chevronLeftImgSrc} />View all literature activities</a>

  const overviewSection = (
    <div className="overview">
      <div>
        <h1>{config.title}</h1>
        {config.description.map((paragraph, i) => <p className="overview-description" key={i}>{paragraph}</p>)}
      </div>
      <div className="header-image-container">
        <img alt="" src={fishtankGradeHeaderImgSrc} />
        <Tooltip tooltipText={FISHTANK_IMAGE_CREDIT_TOOLTIP_TEXT} tooltipTriggerText="Image credit" tooltipTriggerTextClass="image-attribution-tooltip" />
      </div>
    </div>
  )

  const partnerSection = (
    <div className="partner-section">
      <h2>Paired with Fishtank Learning&apos;s English Language Arts Curriculum</h2>
      <p>Quill.org &amp; Fishtank Learning, two nonprofit organizations dedicated to building literacy and critical thinking skills, have partnered to integrate AI-powered writing instruction directly into Fishtank ELA. Each Quill activity can be used with a Fishtank lesson to help students build background knowledge and practice sentence-to-essay writing skills. Want to learn more? Visit <a href="https://www.fishtanklearning.org/" rel="noopener noreferrer" target="_blank">fishtanklearning.org</a></p>
    </div>
  )

  return (
    <CoursePage
      activitiesHeader={FISHTANK_ELA_ALIGNED_QUILL_ACTIVITIES}
      contentPageLink={contentPageLink}
      courseType={LITERATURE_COURSE_PAGE}
      localStorageKey={config.localStorageKey}
      overviewSection={overviewSection}
      partnerSection={partnerSection}
      subscriptionType={subscriptionType}
      unitTemplatePath={config.unitTemplatePath}
    />
  )
}

export default LiteratureGradeLevel;
